import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { TIERS, TIER_ORDER } from "@/lib/tiers";
import { CheckCircle2, X, Sparkles, Loader2, Compass } from "lucide-react";

const COPY = {
  en: {
    kicker: "DIY Legal",
    heading: "Know Where You Are. Know What's Next. Be Court Ready.",
    sub: "Every plan is a one-time yearly charge — no auto-renew, no monthly bills. Pick the level of support that fits how you want to navigate your case.",
    current: "Your current plan",
    signin: "Sign in to start",
    unlock: (name) => `Get ${name}`,
    loading: "Redirecting...",
    matrix_heading: "Feature comparison",
    close: "Not included",
    faq_heading: "Common questions",
    faq: [
      { q: "Do plans auto-renew?", a: "No. Every plan is a single yearly charge. You decide each year whether to renew — nothing charges automatically." },
      { q: "Can I upgrade later?", a: "Yes. Upgrade any time and the new plan takes over immediately. We do not prorate — the higher plan's 12-month term starts from the day you upgrade." },
      { q: "What counts as an 'upload'?", a: "Each document you paste or upload for a plain-English explanation. Practice sessions with the Courtroom Coach are unlimited on every plan." },
      { q: "Is DIY Legal legal advice?", a: "No. DIY Legal provides educational and procedural information only. It is not a law firm and does not provide legal advice, representation, or strategy. Always verify with the court or a licensed attorney." },
    ],
  },
  es: {
    kicker: "DIY Legal",
    heading: "Sabe dónde estás. Sabe qué sigue. Prepárate para la corte.",
    sub: "Cada plan es un cargo único anual — sin renovación automática, sin cargos mensuales. Elige el nivel de apoyo que necesitas.",
    current: "Tu plan actual",
    signin: "Inicia sesión para empezar",
    unlock: (name) => `Obtener ${name}`,
    loading: "Redirigiendo...",
    matrix_heading: "Comparación de funciones",
    close: "No incluido",
    faq_heading: "Preguntas frecuentes",
    faq: [
      { q: "¿Los planes se renuevan automáticamente?", a: "No. Cada plan es un cargo anual único. Tú decides cada año si renuevas — nada se cobra automáticamente." },
      { q: "¿Puedo subir de nivel después?", a: "Sí. Sube de nivel en cualquier momento y el nuevo plan empieza de inmediato. No prorrateamos — el plazo de 12 meses del plan superior empieza el día que subes." },
      { q: '¿Qué cuenta como una "carga"?', a: "Cada documento que pegas o subes para recibir una explicación en lenguaje sencillo. Las sesiones de práctica del Entrenador de Sala son ilimitadas en cada plan." },
      { q: "¿DIY Legal es asesoría legal?", a: "No. DIY Legal ofrece únicamente información educativa y procesal. No es un bufete de abogados y no ofrece asesoría, representación ni estrategia legal. Siempre verifica con la corte o con un abogado con licencia." },
    ],
  },
};

const MATRIX = {
  en: [
    { row: "Document uploads", vals: ["10", "24 / year", "120 / year", "Unlimited"] },
    { row: "I Got Served. Now What?", vals: [true, true, true, true] },
    { row: "Case Timeline", vals: ["Basic", "Expanded", "Expanded", "Expanded"] },
    { row: "Explain My Document", vals: ["Basic", "Expanded", "Expanded", "Expanded"] },
    { row: "Courtroom Coach", vals: [true, true, true, true] },
    { row: "What Happens Next?", vals: [true, true, true, true] },
    { row: "Email / text support", vals: [false, true, true, "Priority"] },
    { row: "Community Court Navigator help", vals: [false, "Support", "Expanded", "12 sessions / yr"] },
    { row: "Legal Research Assistant", vals: [false, false, true, true] },
    { row: "Document & Argument Builder", vals: [false, false, true, true] },
    { row: "Court Ready book (digital)", vals: [false, false, false, true] },
  ],
  es: [
    { row: "Cargas de documentos", vals: ["10", "24 / año", "120 / año", "Ilimitado"] },
    { row: "Me demandaron. ¿Y ahora qué?", vals: [true, true, true, true] },
    { row: "Cronología del caso", vals: ["Básica", "Ampliada", "Ampliada", "Ampliada"] },
    { row: "Explicar mi documento", vals: ["Básica", "Ampliada", "Ampliada", "Ampliada"] },
    { row: "Entrenador de sala", vals: [true, true, true, true] },
    { row: "¿Qué pasa después?", vals: [true, true, true, true] },
    { row: "Soporte por correo / texto", vals: [false, true, true, "Prioritario"] },
    { row: "Ayuda de Navegadora Comunitaria", vals: [false, "Apoyo", "Ampliado", "12 sesiones / año"] },
    { row: "Asistente de Investigación Legal", vals: [false, false, true, true] },
    { row: "Constructor de Documentos y Argumentos", vals: [false, false, true, true] },
    { row: "Libro Court Ready (digital)", vals: [false, false, false, true] },
  ],
};

export default function Pricing() {
  const { user } = useAuth();
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;
  const catalog = TIERS[lang] || TIERS.en;
  const matrix = MATRIX[lang] || MATRIX.en;
  const [loading, setLoading] = useState(null);
  const [err, setErr] = useState("");
  const signedIn = user && typeof user === "object";
  const currentTier = signedIn ? (user.tier || "free") : "free";

  const buy = async (lookup_key) => {
    if (!signedIn) return;
    setErr("");
    setLoading(lookup_key);
    try {
      const res = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
        user_id: user.id,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      setErr(e.response?.data?.detail || "Something went wrong. Please try again.");
      setLoading(null);
    }
  };

  const cellVal = (v) => {
    if (v === true) return <CheckCircle2 className="w-5 h-5 text-emerald-600 mx-auto" aria-label="Included" />;
    if (v === false) return <X className="w-5 h-5 text-[#B8B4AC] mx-auto" aria-label={c.close} />;
    return <span className="text-sm font-semibold text-[#1C2024]">{v}</span>;
  };

  return (
    <div className="max-w-6xl mx-auto py-10 px-4" data-testid="pricing-page">
      <div className="text-center max-w-3xl mx-auto">
        <div className="text-xs uppercase tracking-[0.25em] text-[#C85A32] font-extrabold inline-flex items-center gap-2">
          <Compass className="w-3.5 h-3.5" /> {c.kicker}
        </div>
        <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#1C2024] leading-tight">{c.heading}</h1>
        <p className="mt-4 text-[#4A525A] text-base sm:text-lg leading-relaxed">{c.sub}</p>
      </div>

      {/* Plan cards */}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {TIER_ORDER.map((tKey) => {
          const t = catalog[tKey];
          const isCurrent = currentTier === tKey;
          const highlighted = tKey === "complete" || tKey === "court_ready_plus";
          return (
            <div
              key={tKey}
              data-testid={`pricing-card-${tKey}`}
              className={`relative rounded-2xl border-2 p-6 flex flex-col ${
                tKey === "court_ready_plus"
                  ? "border-[#C85A32] bg-[#FAF5EC] shadow-lg"
                  : highlighted
                    ? "border-[#2C4A3E] bg-white shadow-sm"
                    : "border-[#E4DDD3] bg-white"
              }`}
            >
              {t.badge && (
                <span className={`absolute -top-2.5 right-4 text-[10px] font-extrabold uppercase tracking-widest text-white px-2.5 py-0.5 rounded-full ${
                  tKey === "court_ready_plus" ? "bg-emerald-600" : "bg-emerald-500"
                }`}>
                  {t.badge}
                </span>
              )}
              <div className="text-xs uppercase tracking-widest text-[#707880] font-semibold">DIY Legal {t.name}</div>
              <div className="mt-2 flex items-baseline gap-1.5 flex-wrap">
                <span className="text-4xl font-extrabold text-[#1C2024]" data-testid={`pricing-${tKey}-price`}>{t.price}</span>
                <span className="text-sm text-[#4A525A]">{t.period}</span>
              </div>
              <div className="mt-2 text-sm font-semibold text-[#C85A32]">{t.uploads_label}</div>
              <p className="mt-3 text-sm text-[#4A525A]">{t.tagline}</p>
              <ul className="mt-4 space-y-1.5 text-sm text-[#1C2024]">
                {t.features.map((f, i) => (
                  <li key={i} className="flex gap-2">
                    <CheckCircle2 className="w-4 h-4 shrink-0 text-[#C85A32] mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-auto pt-5">
                {tKey === "free" ? (
                  isCurrent ? (
                    <div className="text-center text-xs font-extrabold uppercase tracking-widest text-[#707880] py-3 border-2 border-dashed border-[#E4DDD3] rounded-xl">{c.current}</div>
                  ) : (
                    <Link
                      to={signedIn ? "/summarize" : "/register"}
                      data-testid={`pricing-${tKey}-cta`}
                      className="pod-focus w-full text-center bg-[#1C2024] hover:bg-[#000] text-white font-bold py-3 rounded-xl inline-flex items-center justify-center"
                    >
                      Start free
                    </Link>
                  )
                ) : isCurrent ? (
                  <div className="text-center text-xs font-extrabold uppercase tracking-widest text-[#707880] py-3 border-2 border-dashed border-[#E4DDD3] rounded-xl" data-testid={`pricing-${tKey}-current`}>
                    {c.current}
                  </div>
                ) : signedIn ? (
                  <button
                    onClick={() => buy(t.lookup_key)}
                    disabled={loading !== null}
                    data-testid={`pricing-${tKey}-buy`}
                    className={`pod-focus w-full font-bold py-3 rounded-xl inline-flex items-center justify-center gap-2 disabled:opacity-70 ${
                      tKey === "court_ready_plus"
                        ? "bg-[#C85A32] hover:bg-[#B24C28] text-white"
                        : "bg-[#1C2024] hover:bg-[#000] text-white"
                    }`}
                  >
                    {loading === t.lookup_key ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {loading === t.lookup_key ? c.loading : c.unlock(t.name)}
                  </button>
                ) : (
                  <Link
                    to="/login"
                    data-testid={`pricing-${tKey}-signin`}
                    className="pod-focus w-full text-center bg-[#1C2024] hover:bg-[#000] text-white font-bold py-3 rounded-xl inline-flex items-center justify-center"
                  >
                    {c.signin}
                  </Link>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {err && <div className="mt-6 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 max-w-2xl mx-auto text-center" data-testid="pricing-error">{err}</div>}

      {/* Feature matrix */}
      <div className="mt-14" data-testid="pricing-matrix">
        <h2 className="text-2xl font-extrabold text-[#1C2024] text-center">{c.matrix_heading}</h2>

        {/* Mobile: stacked per-tier accordion — no horizontal scroll */}
        <div className="mt-6 md:hidden space-y-3" data-testid="pricing-matrix-mobile">
          {TIER_ORDER.map((tKey) => (
            <details
              key={tKey}
              data-testid={`pricing-matrix-mobile-${tKey}`}
              className="group rounded-2xl border-2 border-[#E4DDD3] bg-white overflow-hidden open:border-[#C85A32]"
            >
              <summary className="flex items-center justify-between gap-3 cursor-pointer px-4 py-3 select-none list-none">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#707880] font-extrabold">DIY Legal</div>
                  <div className="text-base font-extrabold text-[#1C2024]">{catalog[tKey].name}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-extrabold text-[#C85A32]">{catalog[tKey].price}</span>
                  <span className="w-6 h-6 rounded-full bg-[#FAF5EC] group-open:bg-[#C85A32] group-open:text-white text-[#C85A32] flex items-center justify-center text-[10px] font-extrabold transition-colors">+</span>
                </div>
              </summary>
              <ul className="border-t border-[#E4DDD3] divide-y divide-[#F0EAE0] bg-white">
                {matrix.map((r, i) => {
                  const v = r.vals[TIER_ORDER.indexOf(tKey)];
                  return (
                    <li key={i} className="flex items-center justify-between gap-3 px-4 py-2.5">
                      <span className="text-sm text-[#1C2024]">{r.row}</span>
                      <span className="text-sm shrink-0 text-right">{cellVal(v)}</span>
                    </li>
                  );
                })}
              </ul>
            </details>
          ))}
        </div>

        {/* Desktop: side-by-side comparison table */}
        <div className="mt-6 rounded-2xl border-2 border-[#E4DDD3] bg-white hidden md:block">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-[#FAF5EC] text-[#1C2024]">
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-extrabold">Feature</th>
                {TIER_ORDER.map((tKey) => (
                  <th key={tKey} className="px-4 py-3 text-xs uppercase tracking-widest font-extrabold text-center">
                    {catalog[tKey].name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.map((r, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[#FBF9F5]"}>
                  <td className="px-4 py-3 text-sm text-[#1C2024] font-semibold">{r.row}</td>
                  {r.vals.map((v, j) => (
                    <td key={j} className="px-4 py-3 text-center">{cellVal(v)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* FAQ */}
      <div className="mt-14" data-testid="pricing-faq">
        <h2 className="text-2xl font-extrabold text-[#1C2024] text-center">{c.faq_heading}</h2>
        <div className="mt-6 max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4">
          {c.faq.map((f, i) => (
            <div key={i} className="rounded-2xl border-2 border-[#E4DDD3] bg-white p-5">
              <div className="font-bold text-[#1C2024]">{f.q}</div>
              <p className="mt-1.5 text-sm text-[#4A525A] leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
